import { Badge } from "@mantine/core"
import { InfoIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { IBackOrderPlus } from "../../models/Order"
import { IReference } from "../../store/General"
import { dateTimeSecFormat } from "../../utils/date"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import { ColumnType, TableActionButton } from "../table"
import { ColumnVisibility } from "../odoo/column-toggle"
import RenderText from "../ui/render-text"

type ColumnDeps = {
  orderStatus: IReference[]
  paymentStatus: IReference[]
  deliveryNoteStatus: IReference[]
  invoiceStatus: IReference[]
  orderPurchaseTypes: IReference[]
  onClick: (key: string, record: any) => void
  ORD_BCK_ORD: any
}

export const useBackOrderColumns = (deps: ColumnDeps): ColumnType<IBackOrderPlus>[] => {
  const { orderStatus, paymentStatus, deliveryNoteStatus, invoiceStatus, orderPurchaseTypes, onClick, ORD_BCK_ORD } = deps
  const { t } = useTranslation(["order", "common"])

  return [
    {
      title: "#",
      render: (_r, index) => <RenderText text={index + 1} />,
      width: "30px",
    },
    {
      title: t("order:backOrder.action"),
      sorter: false,
      render: (record) =>
        ORD_BCK_ORD.hasAccess && (
          <TableActionButton
            icon={<InfoIcon />}
            label={t("order:backOrder.detail")}
            onClick={() => onClick("detail", record)}
          />
        ),
    },
    {
      title: "PO №",
      sorter: true,
      dataIndex: "purchaseCode",
      render: (record) => <RenderText text={record?.purchaseCode} color="var(--mantine-color-dark-5)" />,
    },
    {
      title: t("order:backOrder.poDate"),
      sorter: true,
      dataIndex: "createdAt",
      render: (record) => <RenderText text={dateTimeSecFormat(record?.createdAt)} color="var(--mantine-color-dark-5)" />,
    },
    {
      title: t("order:backOrder.poType"),
      sorter: true,
      dataIndex: "purchaseType",
      render: (record) =>
        record?.purchaseType ? (
          <Badge
            size="md"
            radius="sm"
            variant="outline"
            style={{
              border: `${orderPurchaseTypes.find((c: any) => c.code === record.purchaseType)?.color} 1px solid`,
              color: `${orderPurchaseTypes.find((c: any) => c.code === record.purchaseType)?.color}`,
            }}
            styles={{ label: { overflow: "visible" } }}
          >
            {orderPurchaseTypes.find((c: any) => c?.code === record?.purchaseType)?.name || "-"}
          </Badge>
        ) : (
          <RenderText text={null} color="var(--mantine-color-dark-5)" />
        ),
    },
    {
      title: t("order:backOrder.buyer"),
      sorter: true,
      dataIndex: "senderBusinessPartnerRefCodeBusinessName",
      render: (record) => (
        <div>
          <RenderText text={record?.senderBusiness?.partner?.refCode} color="var(--mantine-color-dark-5)" />
          <RenderText text={renderBusinessName(record?.senderBusiness?.partner, "")} color="dark" />
        </div>
      ),
    },
    {
      title: t("order:backOrder.buyerBusiness"),
      sorter: true,
      dataIndex: "senderBusinessRefCodeProfileName",
      render: (record) => (
        <div>
          <RenderText text={record?.senderBusiness?.refCode} color="var(--mantine-color-dark-5)" />
          <RenderText text={renderBusinessName(record?.senderBusiness, "")} color="dark" />
        </div>
      ),
    },
    {
      title: t("order:backOrder.supplierName"),
      sorter: true,
      dataIndex: "receiverBusinessPartnerRefCodeBusinessName",
      render: (record) => (
        <div>
          <RenderText text={record?.receiverBusiness?.partner?.refCode} color="var(--mantine-color-dark-5)" />
          <RenderText text={renderBusinessName(record?.receiverBusiness?.partner, "")} color="dark" />
        </div>
      ),
    },
    {
      title: t("order:backOrder.supplierBusiness"),
      sorter: true,
      dataIndex: "receiverBusinessRefCodeProfileName",
      render: (record) => (
        <div>
          <RenderText text={record?.receiverBusiness?.refCode} color="var(--mantine-color-dark-5)" />
          <RenderText text={renderBusinessName(record?.receiverBusiness, "")} color="dark" />
        </div>
      ),
    },
    {
      title: t("order:backOrder.poStatus"),
      sorter: true,
      dataIndex: "orderStatus",
      render: (record) => {
        const statusItem = orderStatus.find((c: any) => c.code === record.orderStatus)
        const label = record?.type === "SALES" ? statusItem?.sentName : statusItem?.receivedName
        return (
          <Badge
            size="md"
            radius="sm"
            variant="outline"
            style={{ border: `${statusItem?.color} 1px solid`, color: statusItem?.color }}
            styles={{ label: { overflow: "visible" } }}
          >
            {label || "-"}
          </Badge>
        )
      },
    },
    {
      title: t("order:backOrder.deliveryDate"),
      sorter: true,
      dataIndex: "deliveryDate",
      render: (record) => <RenderText text={record?.deliveryDate ? dateTimeSecFormat(record?.deliveryDate) : null} color="dark" />,
    },
    {
      title: t("order:backOrder.deliveryStatus"),
      sorter: true,
      dataIndex: "deliveryNoteStatus",
      render: (record) =>
        record?.deliveryNote !== null ? (
          <Badge
            size="md"
            radius="sm"
            variant="outline"
            style={{
              border: `${deliveryNoteStatus.find((c: any) => c.code === record?.deliveryNote?.deliveryNoteStatus)?.color} 1px solid`,
              color: `${deliveryNoteStatus.find((c: any) => c.code === record?.deliveryNote?.deliveryNoteStatus)?.color}`,
            }}
            styles={{ label: { overflow: "visible" } }}
          >
            {deliveryNoteStatus.find((c: any) => c?.code === record?.deliveryNote?.deliveryNoteStatus)?.name || "-"}
          </Badge>
        ) : (
          <RenderText text={null} color="var(--mantine-color-dark-5)" />
        ),
    },
    {
      title: t("order:backOrder.deliveryPerson"),
      sorter: true,
      dataIndex: "deliveryNoteStaffFirstNameEmail",
      render: (record) =>
        record?.deliveryNote !== null ? (
          <div>
            <RenderText text={renderName(record?.deliveryNote?.staff)} color="var(--mantine-color-dark-5)" />
            <RenderText text={record?.deliveryNote?.staff?.email} color="dark" />
          </div>
        ) : (
          <RenderText text={null} color="var(--mantine-color-dark-5)" />
        ),
    },
    {
      title: t("order:backOrder.receiverBranch"),
      sorter: true,
      dataIndex: "receiverBranchName",
      render: (record) => <RenderText text={record?.receiverBranch?.name} color="dark" />,
    },
    {
      title: t("order:backOrder.receiverStaff"),
      sorter: true,
      dataIndex: "receiverStaffLastNameFirstName",
      render: (record) => <RenderText text={renderName(record?.receiverStaff)} color="dark" />,
    },
    {
      title: t("order:backOrder.orderAmount"),
      sorter: true,
      dataIndex: "orderAmount",
      render: (record) => <RenderText text={record?.orderAmount || 0} isAmount color="var(--mantine-color-dark-5)" />,
    },
    {
      title: t("order:backOrder.paidAmount"),
      sorter: true,
      dataIndex: "paidAmount",
      render: (record) => <RenderText text={record?.paidAmount || 0} isAmount color="var(--mantine-color-dark-5)" />,
    },
    {
      title: t("order:backOrder.remainingAmount"),
      sorter: true,
      dataIndex: "amountToPay",
      render: (record) => <RenderText text={record?.amountToPay || 0} isAmount color="var(--mantine-color-dark-5)" />,
    },
    {
      title: t("order:backOrder.paymentStatus"),
      sorter: true,
      dataIndex: "paymentStatus",
      render: (record) =>
        record?.invoiceId !== null ? (
          <Badge
            size="md"
            radius="sm"
            variant="outline"
            style={{
              border: `${paymentStatus.find((c: any) => c.code === record?.invoice?.paymentStatus)?.color} 1px solid`,
              color: `${paymentStatus.find((c: any) => c.code === record?.invoice?.paymentStatus)?.color}`,
            }}
            styles={{ label: { overflow: "visible" } }}
          >
            {paymentStatus.find((c: any) => c.code === record?.invoice?.paymentStatus)?.name || "-"}
          </Badge>
        ) : (
          <RenderText text={null} color="var(--mantine-color-dark-5)" />
        ),
    },
    {
      title: t("order:backOrder.invoiceNo"),
      sorter: true,
      dataIndex: "invoiceRefCode",
      render: (record) =>
        record?.invoiceId !== null ? (
          <RenderText text={record?.invoice?.refCode} color="dark" />
        ) : (
          <RenderText text={null} color="var(--mantine-color-dark-5)" />
        ),
    },
    {
      title: t("order:backOrder.invoiceStatus"),
      sorter: true,
      dataIndex: "invoiceStatus",
      render: (record) =>
        record?.invoiceId !== null ? (
          <Badge
            size="md"
            radius="sm"
            variant="outline"
            style={{
              border: `${invoiceStatus.find((c: any) => c.code === record?.invoice?.invoiceStatus)?.color} 1px solid`,
              color: `${invoiceStatus.find((c: any) => c.code === record?.invoice?.invoiceStatus)?.color}`,
            }}
            styles={{ label: { overflow: "visible" } }}
          >
            {invoiceStatus.find((c: any) => c.code === record?.invoice?.invoiceStatus)?.name || "-"}
          </Badge>
        ) : (
          <RenderText text={null} color="var(--mantine-color-dark-5)" />
        ),
    },
    {
      title: t("order:backOrder.invoiceApproved"),
      sorter: true,
      dataIndex: "repondedUser",
      render: (record) => (
        <RenderText
          text={
            (record?.type === "SALES" && record?.orderStatus === "AUTHORIZED") || record?.orderStatus === "APPROVED"
              ? renderName(record?.respondedUser)
              : renderName(record?.reviewedUser)
          }
        />
      ),
    },
    {
      title: t("order:backOrder.paymentTerm"),
      sorter: true,
      dataIndex: "paymentTermDescription",
      render: (record) => <RenderText text={record?.paymentTerm?.description} color="var(--mantine-color-dark-5)" />,
    },
    {
      title: t("order:backOrder.poCreatedBy"),
      sorter: true,
      dataIndex: "orderedUserLastNameFirstName",
      render: (record) => (
        <RenderText text={record?.type === "PURCHASE" ? renderName(record?.orderedUser) : null} color="var(--mantine-color-dark-5)" />
      ),
    },
    {
      title: t("order:backOrder.poReviewed"),
      sorter: true,
      dataIndex: "revieweduser",
      render: (record) => (
        <RenderText
          text={
            (record?.type === "PURCHASE" && record?.orderStatus === "AUTHORIZED") || record?.orderStatus === "APPROVED"
              ? renderName(record?.respondedUser)
              : renderName(record?.reviewedUser)
          }
          color="var(--mantine-color-dark-5)"
        />
      ),
    },
  ]
}

export const getBackOrderColumnKeys = (t: (key: string) => string): ColumnVisibility[] => [
  { key: "purchaseCode", label: "PO №", visible: true, locked: true },
  { key: "createdAt", label: t("supplierSales:dropshipping.columns.poDate"), visible: true },
  { key: "purchaseType", label: t("supplierSales:dropshipping.columns.poType"), visible: true },
  { key: "senderBusinessPartnerRefCodeBusinessName", label: t("supplierSales:purchase.columns.buyer"), visible: true },
  { key: "senderBusinessRefCodeProfileName", label: t("supplierSales:customer.businessName"), visible: true },
  { key: "receiverBusinessPartnerRefCodeBusinessName", label: t("supplierSales:customer.partnerName"), visible: true },
  { key: "receiverBusinessRefCodeProfileName", label: t("supplierSales:customer.businessName"), visible: true },
  { key: "orderStatus", label: t("supplierSales:dropshipping.columns.poStatus"), visible: true, locked: true },
  { key: "deliveryDate", label: t("supplierSales:dropshipping.columns.deliveryDate"), visible: true },
  { key: "deliveryNoteStatus", label: t("supplierSales:dropshipping.columns.deliveryStatus"), visible: true },
  { key: "deliveryNoteStaffFirstNameEmail", label: t("supplierSales:dropshipping.columns.deliveryAssigned"), visible: true },
  { key: "receiverBranchName", label: t("supplierSales:customer.receiverBranch"), visible: true },
  { key: "receiverStaffLastNameFirstName", label: t("supplierSales:customer.receiverStaff"), visible: true },
  { key: "orderAmount", label: t("supplierSales:purchase.columns.amount"), visible: true },
  { key: "paidAmount", label: t("supplierSales:dropshipping.columns.paidAmount"), visible: true },
  { key: "amountToPay", label: t("supplierSales:dropshipping.columns.amountToPay"), visible: true },
  { key: "paymentStatus", label: t("supplierSales:purchase.columns.paymentStatus"), visible: true },
  { key: "invoiceRefCode", label: t("supplierSales:dropshipping.columns.invoiceNumber"), visible: true },
  { key: "invoiceStatus", label: t("supplierSales:dropshipping.columns.invoiceStatus"), visible: true },
  { key: "repondedUser", label: t("supplierSales:dropshipping.columns.reviewedBy"), visible: true },
  { key: "paymentTermDescription", label: t("supplierSales:dropshipping.columns.paymentTerm"), visible: true },
  { key: "orderedUserLastNameFirstName", label: t("supplierSales:dropshipping.columns.ordered"), visible: true },
  { key: "revieweduser", label: t("supplierSales:dropshipping.columns.reviewedBy"), visible: true },
]

export function filterVisibleBackOrderColumns(columns: ColumnType<IBackOrderPlus>[], visibility: ColumnVisibility[]): ColumnType<IBackOrderPlus>[] {
  const visibleKeys = new Set(visibility.filter((v) => v.visible).map((v) => v.key))
  return columns.filter((col) => {
    if (!col.dataIndex) return true
    return visibleKeys.has(col.dataIndex)
  })
}
